import type { AxiosRequestConfig, CancelTokenSource } from 'axios';
import axios from 'axios';
import xorBy from 'lodash/xorBy';
import { defineStore, storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { getBetBuilderTicketGroupScore, isHandicap, isShowPointDisplayType } from '@/components/betBuilder/betBuilderOddsDisplay';
import type { IBetBuilderMatchInfo } from '@/interface/IBetBuilderRecommendation';
import type { IEventResultScore } from '@/interface/IMatch';
import type { IPrice } from '@/interface/IPrice';
import type { IScore } from '@/interface/IScore';
import type { IBetBuilderBetSlipMarket, IBetBuilderMarket, IBetBuilderSelection, IBetSlipSelection } from '@/interface/betBuilder';
import type { IBetBuilderBetSlip, IBetBuilderTicket } from '@/interface/betSlip';
import {
    BetBuilderSelectionStatus,
    BetBuilderSelectionType,
    BetBuilderTicketStatus,
    BetPageType,
    BetSlipState,
    BetSlipType,
    MarketPage,
    PlaceOrderStatus,
    SportType,
    StatementDetailStatus,
} from '@/interface/enum';
import type { BetBuilderSelectionRequest, BetBuilderTicketRequest } from '@/interface/pollux';
import Api, { fireAndForget } from '@/libraries/api';
import { createMapping } from '@/libraries/array';
import { getBetBuilderOpenTicketErrorMessage, getPlaceBetErrorMessage } from '@/libraries/betSlip/betSlipErrorMessage';
import { saveLastStake } from '@/libraries/betSlip/lastStakeStorage';
import { calculateStake, isStakeAtLeastMinBet, isStakeLessBalance, isStakeWithinMaxBet } from '@/libraries/betSlip/stake';
import { useAutoResetState } from '@/libraries/composables/useAutoResetState';
import { useBetBuilderLegSetting } from '@/libraries/composables/useBetBuilderLegSetting';
import useState from '@/libraries/composables/useState';
import { platform } from '@/libraries/helpers/platformHelper';
import { isScoreChanged } from '@/libraries/helpers/scoreHelper';
import { getTranslated } from '@/libraries/helpers/translationHelper';
import { getMarketRule } from '@/libraries/oddsApi/betBuilderMarketRule';
import { hasBetBuilderScoreGroup } from '@/libraries/oddsApi/betBuilderScoreSource';
import { getSportsBsiUrl } from '@/libraries/url';
import { formatMinDecimalPlace, toFixedDecimalPoint } from '@/libraries/utils/text';
import { sleep } from '@/libraries/utils/utils';
import { useAccountStore } from '@/stores/accountStore';
import { useBetCountStore } from '@/stores/betCountStore';
import { useBetSlipStore } from '@/stores/betSlip/betSlipStore';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';
import { useRootStore } from '@/stores/rootStore';

interface IOpenTicketResult {
    matchId: number;
    markets: IBetBuilderBetSlipMarket[];
    minBet: number;
    maxBet: number;
    balance: number | null;
    price: IPrice | null;
    status: BetBuilderTicketStatus;
    selectedSelections: { selectionId: number, status: BetBuilderSelectionStatus, isRedundant?: boolean }[];
    uid: string;
    mainScore: IScore | null;
    selectionLiveScores: Map<number, IScore>;
}

interface IScoreChange {
    oldScore: IScore;
    newScore: IScore;
}

function isBettablePrice(price: IPrice | null): price is IPrice {
    return price !== null && Number.isFinite(price.value) && price.value !== 0;
}

async function clearServerTicket() {
    try {
        await Api.clearTicket();
    } catch (error) {
        console.error(error);
    }
}

export const useBetBuilderBetSlipStore = defineStore('BetBuilderBetSlip', () => {
    const betSlip = ref<IBetBuilderBetSlip>(getDefaultBetBuilderBetSlip());

    const [openFrom, setOpenFrom] = useState(BetPageType.Unknown);

    const isUpdatingTicket = ref(false);
    const isAutoRefresh = ref(false);
    const isPlacingBet = ref(false);
    let cancelToken: CancelTokenSource;
    let pendingBetBuilderReset: Promise<void> | undefined;
    const stake = ref(0);
    const setStake = (value: number) => {
        stake.value = value;
    };

    const [errorMessage] = useAutoResetState({
        status: '',
        key: '',
    }, 3000);

    const { placeBetDelayTime } = storeToRefs(useFeatureToggleStore());
    const leagueId = computed(() => betSlip.value.match.leagueId);
    const { minLegCount } = useBetBuilderLegSetting(leagueId);
    const {
        setTopScreenToast,
        setShowAcceptAnyOddsBanner,
        setFailedPlaceBetShowAaoBanner,
    } = useRootStore();

    const { setBetSlipState, setBetSlipType } = useBetSlipStore();

    const {
        formatMoneyWithSymbolAndDecimal,
        setBalance,
    } = useAccountStore();
    const { balance } = storeToRefs(useAccountStore());
    const { refreshBetCount } = useBetCountStore();

    const setIsPlacingBet = (value: boolean) => {
        isPlacingBet.value = value;
    };

    const minBet = computed(() => betSlip.value.minBet);
    const maxBet = computed(() => betSlip.value.maxBet);
    const isLive = computed(() => betSlip.value.match.isLive);

    const ticketCount = computed(() => betSlip.value.tickets.length);
    const totalStakeDisplay = computed(() => formatMoneyWithSymbolAndDecimal(stake.value));
    const price = computed(() => betSlip.value.price?.value ?? 0);
    const hasBettablePrice = computed(() => isBettablePrice(betSlip.value.price));
    const totalOddsDisplay = computed(() => (betSlip.value.price?.value ? formatMinDecimalPlace(betSlip.value.price.value, 2) : '--'));
    const totalPayoutDisplay = computed(() => formatMoneyWithSymbolAndDecimal(price.value * stake.value));

    const isValidSubBetCount = computed(() => ticketCount.value >= minLegCount.value);
    const countOfAddMore = computed(() => (isValidSubBetCount.value ? 0 : minLegCount.value - ticketCount.value));

    const selectionIds = computed(() => betSlip.value.tickets.map(ticket => ticket.selection.id));

    // Markets with their own score group (e.g. total corner) settle against that group's score, so each
    // selection carries the score of its own market group. A selection whose group score is unknown falls
    // back to 0:0 rather than the main match goals, which are a different stat — same rule as Kratos'
    // getTicketLiveScore.
    const getSelectionLiveScore = (ticket: IBetBuilderTicket): IScore => {
        const { isLive, eventResults, liveHomeScore, liveAwayScore } = betSlip.value.match;

        if (!hasBetBuilderScoreGroup(ticket.marketType)) {
            return { home: liveHomeScore, away: liveAwayScore };
        }

        const groupScore = getBetBuilderTicketGroupScore(isLive, ticket.marketType, eventResults);

        return groupScore
            ? { home: groupScore.liveHomeScore, away: groupScore.liveAwayScore }
            : { home: 0, away: 0 };
    };

    const getOrderSelections = (): BetBuilderSelectionRequest[] => betSlip.value.tickets.map(ticket => ({
        id: ticket.selection.id,
        liveScore: betSlip.value.selectionLiveScores.get(ticket.selection.id) ?? getSelectionLiveScore(ticket),
    }));
    const requestPlaceBet = () => Api.placeBetBuilder({
        stake: stake.value,
        uid: betSlip.value.uid,
        betPage: openFrom.value,
        selections: getOrderSelections(),
    });

    const isValidStake = computed(() => hasBettablePrice.value
        && isStakeWithinMaxBet(stake.value, betSlip.value.maxBet)
        && isStakeAtLeastMinBet(stake.value, betSlip.value.minBet)
        && isStakeLessBalance(stake.value, balance.value, price.value)
        && stake.value > 0);

    async function resetBetBuilderForSingleMode() {
        if (pendingBetBuilderReset) {
            await pendingBetBuilderReset;
            return;
        }
        if (ticketCount.value === 0) {
            return;
        }
        cancelToken?.cancel('Bet Builder Ticket cleared for SingleMode odds');
        betSlip.value = getDefaultBetBuilderBetSlip();
        setStake(0);
        setOpenFrom(BetPageType.Unknown);
        isUpdatingTicket.value = false;
        isAutoRefresh.value = false;
        pendingBetBuilderReset = clearServerTicket();
        try {
            await pendingBetBuilderReset;
        } finally {
            pendingBetBuilderReset = undefined;
        }
    }

    const removeBetSlip = async () => {
        betSlip.value = getDefaultBetBuilderBetSlip();
        setOpenFrom(BetPageType.Unknown);
        fireAndForget(Api.clearTicket());
        isUpdatingTicket.value = false;
        isAutoRefresh.value = false;
        setBetSlipState(BetSlipState.Closed);
        setBetSlipType(BetSlipType.None);
    };

    const placeBet = async () => {
        const currentPrice = betSlip.value.price;
        if (!isBettablePrice(currentPrice)) {
            return;
        }
        if (!isValidStake.value) {
            adjustStake(currentPrice.value);
            return;
        }
        if (isPlacingBet.value) {
            return;
        }

        setIsPlacingBet(true);

        try {
            const [response] = await Promise.all([requestPlaceBet(), sleep(placeBetDelayTime.value)]);
            const {
                isPlaceBetSuccess,
                placeOrderStatus,
                orderStatus,
                isAccountClosed,
            } = response;

            if (isPlaceBetSuccess) {
                setTopScreenToast({
                    status: 'placeBetSuccess',
                    messageKey: orderStatus === StatementDetailStatus.Running ? 'betSlip_placeBetConfirmed' : 'betSlip_placeBetBeingProcessed',
                });

                setTimeout(() => {
                    refreshBetCount(false);
                    platform.updateBalance();
                    window.eventEmitter.emit('refreshBetList');
                }, 1000);

                betSlip.value = getDefaultBetBuilderBetSlip();
                setBetSlipState(BetSlipState.Closed);
                setBetSlipType(BetSlipType.None);
                saveLastStake(stake.value);

                setShowAcceptAnyOddsBanner(true);
            } else if (isAccountClosed) {
                window.location.href = getSportsBsiUrl();
            } else if (placeOrderStatus === PlaceOrderStatus.RetryOpenTicket) {
                await openTicket(false);
                setTopScreenToast({
                    status: 'error',
                    messageKey: 'betSlip_errorMessage_generalFailure',
                });
            } else {
                setTopScreenToast({
                    status: 'error',
                    messageKey: getPlaceBetErrorMessage(placeOrderStatus),
                });
                refreshBetCount(false);
                setFailedPlaceBetShowAaoBanner(true);
            }
        } catch {
            setFailedPlaceBetShowAaoBanner(true);
            setBetSlipState(BetSlipState.Mini);
            setTopScreenToast({
                status: 'error',
                messageKey: 'betSlip_errorMessage_generalFailure',
            });
        } finally {
            betSlip.value = getDefaultBetBuilderBetSlip();
            setIsPlacingBet(false);
            setBetSlipState(BetSlipState.Closed);
            setBetSlipType(BetSlipType.None);
        }
    };

    const getMainScore = (selectionLiveScores: Map<number, IScore>): IScore | null => {
        const mainMarketTicket = betSlip.value.tickets.find(ticket => !hasBetBuilderScoreGroup(ticket.marketType));

        return (mainMarketTicket && selectionLiveScores.get(mainMarketTicket.selection.id)) ?? null;
    };

    const requestOpenTicket = async (request: BetBuilderTicketRequest, option: AxiosRequestConfig): Promise<IOpenTicketResult> => {
        const response = await Api.openBetBuilderTicket(request, option);
        const selectionLiveScores = new Map(response.selectedSelections.map(selection => [selection.selectionId, selection.liveScore]));

        return {
            ...response,
            mainScore: getMainScore(selectionLiveScores),
            selectionLiveScores,
        };
    };

    const getTicketScoreChange = (result: IOpenTicketResult): IScoreChange | null => {
        const changes = [...result.selectionLiveScores]
            .map(([selectionId, newScore]) => ({ oldScore: betSlip.value.selectionLiveScores.get(selectionId), newScore }))
            .filter((change): change is IScoreChange => change.oldScore !== undefined && isScoreChanged(change.oldScore, change.newScore));

        return changes[0] ?? null;
    };
    async function openTicket(useAutoRefresh = false) {
        if ((isUpdatingTicket.value || isAutoRefresh.value) && cancelToken) {
            cancelToken.cancel();
            isUpdatingTicket.value = false;
            isAutoRefresh.value = false;
        }

        isUpdatingTicket.value = true;

        if (useAutoRefresh) {
            isAutoRefresh.value = true;
        }

        const request = {
            isLive: isLive.value,
            selectionIds: selectionIds.value,
        };

        cancelToken = axios.CancelToken.source();
        try {
            const response = await requestOpenTicket(request, { cancelToken: cancelToken.token });
            const availableResponseSelections = response.selectedSelections.filter(selection => selection.status === BetBuilderSelectionStatus.Running && !selection.isRedundant);
            const availableTickets = betSlip.value.tickets.filter(ticket => availableResponseSelections.some(selection => selection.selectionId === ticket.selection.id));

            if ([BetBuilderTicketStatus.Unknown, BetBuilderTicketStatus.MaxLegCountExceeded, BetBuilderTicketStatus.Unavailable, BetBuilderTicketStatus.IncompatibleSelection].includes(response.status)) {
                const scoreChange = getTicketScoreChange(response);

                if (scoreChange) {
                    setTopScreenToast({
                        status: 'error',
                        messageKey: 'betSlip_errorMessage_scoreChanged',
                        messageData: {
                            oldScore: `${scoreChange.oldScore.home}:${scoreChange.oldScore.away}`,
                            newScore: `${scoreChange.newScore.home}:${scoreChange.newScore.away}`,
                        },
                    });
                } else {
                    setTopScreenToast({
                        status: 'error',
                        messageKey: getBetBuilderOpenTicketErrorMessage(response.status),
                    });
                }

                isUpdatingTicket.value = false;
                isAutoRefresh.value = false;
            }

            if (availableTickets.length === 0) {
                await removeBetSlip();
                return;
            }

            if (availableResponseSelections.length !== selectionIds.value.length) {
                const key = getBetBuilderOpenTicketErrorMessage(response.status);
                setTopScreenToast({
                    status: 'error',
                    messageKey: key,
                });
            }

            const availableSelections = response.markets
                .flatMap(market => market.selections)
                .filter(selection => selection.status === BetBuilderSelectionStatus.Running && selection.isCompatible);

            const availableSelectionMap = createMapping(
                availableSelections,
                selection => selection.id,
                selection => selection,
            );

            betSlip.value = {
                minBet: response.minBet,
                maxBet: response.maxBet,
                match: {
                    isLive: isLive.value,
                    matchId: betSlip.value.match.matchId,
                    leagueId: betSlip.value.match.leagueId,
                    marketPage: betSlip.value.match.marketPage,
                    homeTeamName: betSlip.value.match.homeTeamName,
                    awayTeamName: betSlip.value.match.awayTeamName,
                    liveHomeScore: response.mainScore?.home ?? betSlip.value.match.liveHomeScore,
                    liveAwayScore: response.mainScore?.away ?? betSlip.value.match.liveAwayScore,
                    sportType: betSlip.value.match.sportType,
                    leagueName: betSlip.value.match.leagueName,
                    eventResults: betSlip.value.match.eventResults,
                },
                uid: response.uid,
                price: response.price,
                tickets: availableTickets,
                markets: response.markets,
                availableSelectionMap,
                selectionLiveScores: response.selectionLiveScores,
                betPage: betSlip.value.betPage,
            };

            setBalance(response.balance);

            isUpdatingTicket.value = false;
            isAutoRefresh.value = false;
        } catch (error) {
            isUpdatingTicket.value = false;
            isAutoRefresh.value = false;

            if (axios.isCancel(error)) {
                return;
            }

            await removeBetSlip();
            setBetSlipState(BetSlipState.Closed);
            setTopScreenToast({
                status: 'error',
                messageKey: 'betSlip_errorMessage_generalFailure',
            });
        }
    }

    const refreshOnMainScoreChanged = async (newScore: IScore) => {
        const oldScore: IScore = {
            home: betSlip.value.match.liveHomeScore,
            away: betSlip.value.match.liveAwayScore,
        };

        if (!isScoreChanged(oldScore, newScore)) {
            return;
        }

        betSlip.value.match.liveHomeScore = newScore.home;
        betSlip.value.match.liveAwayScore = newScore.away;

        await openTicket(true);
    };

    const removeTicket = (selectionId: number) => {
        betSlip.value.tickets = betSlip.value.tickets.filter(ticket => ticket.selection.id !== selectionId);
    };

    async function removeTicketOrBetSlip(selectionId: number) {
        if (ticketCount.value === 1) {
            await removeBetSlip();
        } else {
            removeTicket(selectionId);
        }
    }

    const setMatchEventResults = (eventResults: IEventResultScore[]) => {
        betSlip.value.match.eventResults = eventResults;
    };

    const setBetSlip = (newBetSlip: IBetBuilderBetSlip) => {
        betSlip.value = {
            ...newBetSlip,
            selectionLiveScores: newBetSlip.selectionLiveScores ?? new Map<number, IScore>(),
            match: {
                ...newBetSlip.match,
                eventResults: newBetSlip.match.eventResults ?? [],
            },
        };
    };

    const { isShowMediumBetSlip } = storeToRefs(useBetSlipStore());
    const isSameMatch = (matchId: number) => betSlip.value.match.matchId === matchId;
    const addTicket = async (selection: IBetBuilderSelection, match: IBetBuilderMatchInfo, market: IBetBuilderMarket) => {
        const state = isShowMediumBetSlip.value
            ? BetSlipState.Medium
            : BetSlipState.Mini;

        if (ticketCount.value > 0 && !isSameMatch(match.eventId)) {
            isUpdatingTicket.value = true;
            betSlip.value = getDefaultBetBuilderBetSlip();
            isUpdatingTicket.value = false;
        }
        if (betSlip.value.tickets.some(ticket => ticket.selection.id === selection.id)) {
            isUpdatingTicket.value = true;
            const isRemoveBetSlip = betSlip.value.tickets.length === 1;
            await removeTicketOrBetSlip(selection.id);
            if (isRemoveBetSlip) {
                setBetSlipState(BetSlipState.Closed);
                setBetSlipType(BetSlipType.None);
            } else {
                setBetSlipState(state);
                setBetSlipType(BetSlipType.BetBuilder);
            }
            isUpdatingTicket.value = false;
            return;
        }

        betSlip.value.tickets = [...betSlip.value.tickets, {
            selection,
            sportType: match.sportType,
            point: market.point,
            marketDisplayName: market.marketType.name,
            displayType: market.displayType,
            marketType: market.marketType.id,
        }];

        betSlip.value.match.homeTeamName = getTranslated(match.homeTeam?.teamName);
        betSlip.value.match.awayTeamName = getTranslated(match.awayTeam?.teamName);
        betSlip.value.match.isLive = match.isLive;
        betSlip.value.match.matchId = match.eventId;
        betSlip.value.match.leagueId = match.leagueId;
        betSlip.value.match.marketPage = match.marketPage;
        betSlip.value.match.sportType = match.sportType;
        betSlip.value.match.leagueName = getTranslated(match.leagueName);
        betSlip.value.match.eventResults = match.eventResults ?? [];

        setBetSlipState(state);
        setBetSlipType(BetSlipType.BetBuilder);
    };

    function adjustStake(odds: number) {
        const {
            maxBet,
            minBet,
        } = betSlip.value;
        const stakeInfo = calculateStake(maxBet, minBet, balance.value, stake.value, odds);
        setStake(stakeInfo.stake);
        setTopScreenToast({
            status: 'error',
            messageKey: stakeInfo.message,
        });
    }

    async function refresh() {
        if (!isValidSubBetCount.value || isPlacingBet.value) {
            return;
        }

        if (isUpdatingTicket.value) {
            return;
        }

        await openTicket(true);
    }

    watch(() => betSlip.value.tickets, async (newTickets, oldTickets) => {
        if (newTickets.length > 0 && xorBy(newTickets, oldTickets, x => x.selection.id).length > 0) {
            await openTicket();
        }
    }, { deep: true });

    function handleBetBuilderMatchUpdated(matchId: number) {
        const hasTickets = betSlip.value.tickets.length !== 0;
        const isSameMatch = betSlip.value.match.matchId === matchId;
        if (hasTickets && isSameMatch) {
            openTicket();
        }
    }

    const convertBetBuilderTicket = (ticket: IBetBuilderTicket) => {
        const { fullNameDisplay } = getMarketRule(ticket.displayType);
        const pointText = calculatePointText(
            ticket.displayType,
            ticket.point,
            ticket.selection.type.id,
        );

        return {
            selection: ticket.selection,
            sportType: ticket.sportType,
            point: pointText,
            marketDisplayName: ticket.marketDisplayName,
            optionName: fullNameDisplay(ticket.selection),
        };
    };

    window.eventEmitter.on('betBuilderMatchUpdated', handleBetBuilderMatchUpdated);

    return {
        betSlip,
        totalOddsDisplay,
        totalPayoutDisplay,
        totalStakeDisplay,
        isUpdatingTicket,
        isValidSubBetCount,
        ticketCount,
        countOfAddMore,
        placeBet,
        isPlacingBet,
        stake,
        minBet,
        maxBet,
        setStake,
        setBetSlip,
        setMatchEventResults,
        addTicket,
        resetBetBuilderForSingleMode,
        removeTicketOrBetSlip,
        removeBetSlip,
        isSameMatch,
        errorMessage,
        openTicket,
        refreshOnMainScoreChanged,
        isAutoRefresh,
        price,
        hasBettablePrice,
        refresh,
        convertBetBuilderTicket,
        openFrom,
        setOpenFrom,
    };
});

function getDefaultBetBuilderBetSlip(): IBetBuilderBetSlip {
    return {
        minBet: 0,
        maxBet: 0,
        match: {
            leagueId: 0,
            marketPage: MarketPage.Unknown,
            matchId: 0,
            isLive: false,
            homeTeamName: '',
            awayTeamName: '',
            liveHomeScore: 0,
            liveAwayScore: 0,
            sportType: SportType.Unknown,
            leagueName: '',
            eventResults: [],
        },
        uid: '',
        price: null,
        tickets: [],
        markets: [],
        betPage: BetPageType.Unknown,
        availableSelectionMap: new Map<number, IBetSlipSelection>(),
        selectionLiveScores: new Map<number, IScore>(),
    };
}

function calculateHandicapPoint(rawPoint: number, selectionTypeId: number): number {
    const absolutePoint = Math.abs(rawPoint);
    const handicapSide = rawPoint < 0 ? BetBuilderSelectionType.Home : BetBuilderSelectionType.Away;
    const shouldNegate = selectionTypeId === handicapSide;

    return shouldNegate ? -absolutePoint : absolutePoint;
}

function calculatePointText(displayType: number, rawPoint: number, selectionTypeId: number): string {
    if (!isShowPointDisplayType(displayType)) {
        return '';
    }

    const adjustedPoint = isHandicap(displayType)
        ? calculateHandicapPoint(rawPoint, selectionTypeId)
        : rawPoint;

    return toFixedDecimalPoint(adjustedPoint, 2);
}
